import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiPackage, FiAlertTriangle } from 'react-icons/fi';

const CATEGORIES = ['Accessories', 'Thread', 'Fabric', 'Buttons', 'Zippers', 'Lining', 'Other'];

const emptyForm = { name: '', category: 'Accessories', price: '', stockQty: '', minAlertQty: 5 };

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [restockProduct, setRestockProduct] = useState(null);
  const [restockQty, setRestockQty] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const r = await api.get('/products');
    setProducts(r.data.products);
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openAdd = () => { setForm(emptyForm); setEditProduct(null); setShowModal(true); };
  const openEdit = (p) => { setForm({ name: p.name, category: p.category, price: p.price, stockQty: p.stockQty, minAlertQty: p.minAlertQty }); setEditProduct(p); setShowModal(true); };

  const handleSave = async () => {
    if (!form.name || !form.price) return toast.error('Name and price are required');
    setSaving(true);
    try {
      if (editProduct) {
        await api.put(`/products/${editProduct._id}`, form);
        toast.success('Product updated');
      } else {
        await api.post('/products', form);
        toast.success('Product added');
      }
      setShowModal(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from inventory?`)) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Product removed');
      fetchProducts();
    } catch { toast.error('Delete failed'); }
  };

  const handleRestock = async () => {
    if (!restockQty || parseInt(restockQty) <= 0) return toast.error('Enter valid quantity');
    try {
      await api.put(`/products/${restockProduct._id}/restock`, { quantity: restockQty });
      toast.success(`Restocked ${restockQty} units`);
      setShowRestockModal(false);
      setRestockQty('');
      fetchProducts();
    } catch { toast.error('Restock failed'); }
  };

  const lowStockCount = products.filter(p => p.stockQty <= p.minAlertQty).length;

  return (
    <div>
      {lowStockCount > 0 && (
        <div className="alert alert-danger" style={{ marginBottom: 16 }}>
          <FiAlertTriangle size={16} />
          <span><strong>{lowStockCount} product(s)</strong> are running low on stock!</span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#64748b' }}>{products.length} products</div>
        <button className="btn btn-accent" onClick={openAdd}><FiPlus size={15} /> Add Product</button>
      </div>

      <div className="card">
        {loading ? <div className="loading"><div className="spinner"></div></div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Min Alert</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 && (
                  <tr><td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-state-icon"><FiPackage size={40} /></div>
                      <h3>No products yet</h3>
                      <p>Add inventory items like buttons, thread, fabric, etc.</p>
                    </div>
                  </td></tr>
                )}
                {products.map(p => {
                  const isLow = p.stockQty <= p.minAlertQty;
                  return (
                    <tr key={p._id}>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td><span className="badge badge-gray">{p.category}</span></td>
                      <td>SAR {p.price?.toFixed(2)}</td>
                      <td>
                        <span style={{ fontWeight: 700, color: isLow ? '#ef4444' : '#10b981', fontSize: 15 }}>
                          {p.stockQty}
                        </span>
                      </td>
                      <td style={{ color: '#94a3b8' }}>{p.minAlertQty}</td>
                      <td>
                        {isLow
                          ? <span className="badge badge-danger">⚠️ Low Stock</span>
                          : <span className="badge badge-success">In Stock</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-outline btn-sm" onClick={() => { setRestockProduct(p); setShowRestockModal(true); }}>
                            + Restock
                          </button>
                          <button className="btn btn-outline btn-sm btn-icon" onClick={() => openEdit(p)}>
                            <FiEdit2 size={13} />
                          </button>
                          <button className="btn btn-outline btn-sm btn-icon"
                            style={{ color: '#ef4444', borderColor: '#fecaca' }}
                            onClick={() => handleDelete(p._id, p.name)}>
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editProduct ? 'Edit Product' : 'Add Product'}</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Product Name <span className="required">*</span></label>
                <input className="form-control" placeholder="e.g. Leather Belt"
                  value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-control" value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Price (SAR) <span className="required">*</span></label>
                  <input type="number" className="form-control" placeholder="0.00"
                    value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Stock Quantity</label>
                  <input type="number" className="form-control" placeholder="0"
                    value={form.stockQty} onChange={e => setForm({ ...form, stockQty: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Min Alert Qty</label>
                  <input type="number" className="form-control" placeholder="5"
                    value={form.minAlertQty} onChange={e => setForm({ ...form, minAlertQty: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-accent" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : (editProduct ? 'Update' : 'Add Product')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restock Modal */}
      {showRestockModal && restockProduct && (
        <div className="modal-overlay" onClick={() => setShowRestockModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">📦 Restock: {restockProduct.name}</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setShowRestockModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 13 }}>
                Current Stock: <strong>{restockProduct.stockQty}</strong> units
              </div>
              <div className="form-group">
                <label className="form-label">Add Quantity</label>
                <input type="number" className="form-control" placeholder="e.g. 50"
                  value={restockQty} onChange={e => setRestockQty(e.target.value)} autoFocus />
              </div>
              {restockQty && (
                <div style={{ color: '#10b981', fontSize: 13 }}>
                  New total: <strong>{restockProduct.stockQty + parseInt(restockQty || 0)}</strong> units
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowRestockModal(false)}>Cancel</button>
              <button className="btn btn-success" onClick={handleRestock}>Add Stock</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}